import { useState, useMemo, useCallback } from 'react';
import { Search, Plus, BookOpen, Trash2, ChevronDown } from 'lucide-react';
import {
  useRecipes,
  useRecipe,
  useCreateRecipe,
  useUpdateRecipe,
  useAddRecipeMaterial,
  useUpdateRecipeMaterial,
  useRemoveRecipeMaterial,
  useDeleteRecipe,
  type RecipeListDTO,
} from '../../hooks/useRecipes';
import { useMaterials } from '../../hooks/useMaterials';
import { useAuthStore } from '../../stores/auth-store';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Modal } from '../../components/ui/Modal';
import { SkeletonTable } from '../../components/ui/Skeleton';
import { toast } from '../../components/ui/Toast';
import { generateItemCode } from '../../lib/item-utils';

type CategoriaTab = 'todos' | 'gypsum' | 'pintura' | 'impermeabilizacion' | 'empaste';

const CATEGORIA_LABELS: Record<CategoriaTab, string> = {
  todos: 'Todos',
  gypsum: 'Gypsum',
  pintura: 'Pintura',
  impermeabilizacion: 'Impermeabilización',
  empaste: 'Empaste',
};

const UNIDAD_OPTIONS = [
  { value: 'm2', label: 'm²' },
  { value: 'ml', label: 'ml' },
  { value: 'unid', label: 'Unidad' },
  { value: 'kg', label: 'kg' },
  { value: 'glb', label: 'Global' },
];

const CATEGORIA_OPTIONS_LOCAL = [
  { value: 'gypsum', label: 'Gypsum' },
  { value: 'pintura', label: 'Pintura' },
  { value: 'impermeabilizacion', label: 'Impermeabilización' },
  { value: 'empaste', label: 'Empaste' },
];

export function RecipesTab() {
  const user = useAuthStore((s) => s.user);
  const canEdit = ['administrativo', 'gerente', 'operaciones'].includes(user?.role ?? '');
  const canCreate = canEdit || user?.role === 'residente';
  const canEditName = canEdit || user?.role === 'residente';

  const [activeTab, setActiveTab] = useState<CategoriaTab>('todos');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [debounceTimer, setDebounceTimer] = useState<ReturnType<typeof setTimeout>>();
  const [page, setPage] = useState(1);
  const [selectedRecipeId, setSelectedRecipeId] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearch(value);
      if (debounceTimer) clearTimeout(debounceTimer);
      const timer = setTimeout(() => {
        setDebouncedSearch(value);
        setPage(1);
      }, 300);
      setDebounceTimer(timer);
    },
    [debounceTimer],
  );

  const filters = useMemo(
    () => ({
      search: debouncedSearch || undefined,
      categoria: activeTab === 'todos' ? undefined : activeTab,
      isActive: true,
      page,
      limit: 20,
    }),
    [debouncedSearch, activeTab, page],
  );

  const { data, isLoading, isError, refetch } = useRecipes(filters);
  const tabs: CategoriaTab[] = ['todos', 'gypsum', 'pintura', 'impermeabilizacion', 'empaste'];

  if (isLoading) {
    return <Card><div className="p-6"><SkeletonTable rows={5} cols={5} /></div></Card>;
  }

  if (isError) {
    return (
      <Card>
        <div className="px-6 py-12 text-center">
          <p className="text-sm text-gray-500">Error al cargar APUs</p>
          <Button variant="secondary" className="mt-4" onClick={() => refetch()}>Reintentar</Button>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-end">
        {canCreate && (
          <Button variant="primary" leftIcon={<Plus size={16} />} onClick={() => setShowCreateModal(true)}>
            Nuevo APU
          </Button>
        )}
      </div>

      {/* Search + Tabs */}
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          className="w-full rounded-lg border border-gray-200 bg-white/80 py-2 pl-9 pr-3 text-sm placeholder:text-gray-400 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
          placeholder="Buscar por nombre o código..."
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
        />
      </div>

      <div className="flex gap-2 overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab}
            className={`whitespace-nowrap rounded-full px-4 py-1.5 text-xs font-medium transition-colors ${
              activeTab === tab ? 'bg-brand-blue text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            onClick={() => { setActiveTab(tab); setPage(1); }}
          >
            {CATEGORIA_LABELS[tab]}
          </button>
        ))}
      </div>

      {/* Master-Detail Layout */}
      {data && data.data.length === 0 && !debouncedSearch ? (
        <Card>
          <div className="px-6 py-16 text-center">
            <BookOpen size={48} className="mx-auto text-gray-300" />
            <h2 className="mt-4 text-lg font-semibold text-gray-600">No hay APUs aún</h2>
            <p className="mt-1 text-sm text-gray-400">Crea el primer APU del catálogo.</p>
            {canCreate && (
              <Button variant="primary" className="mt-4" leftIcon={<Plus size={16} />} onClick={() => setShowCreateModal(true)}>
                Crear APU
              </Button>
            )}
          </div>
        </Card>
      ) : data && data.data.length === 0 && debouncedSearch ? (
        <Card>
          <div className="px-6 py-16 text-center">
            <Search size={48} className="mx-auto text-gray-300" />
            <h2 className="mt-4 text-lg font-semibold text-gray-600">Sin resultados</h2>
            <p className="mt-1 text-sm text-gray-400">
              No encontramos APUs que coincidan con &quot;{debouncedSearch}&quot;
            </p>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          {/* Master: Recipe List */}
          <div className="lg:col-span-1">
            <Card>
              <div className="divide-y divide-gray-50">
                {data?.data.map((recipe) => (
                  <button
                    key={recipe.id}
                    className={`w-full px-4 py-3 text-left transition-colors hover:bg-gray-50/50 ${
                      selectedRecipeId === recipe.id ? 'bg-brand-blue/5 border-l-2 border-brand-blue' : ''
                    }`}
                    onClick={() => setSelectedRecipeId(recipe.id)}
                  >
                    <RecipeListItem recipe={recipe} />
                  </button>
                ))}
              </div>

              {data && data.pagination.totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 border-t border-gray-100 p-3">
                  <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Ant.</Button>
                  <span className="text-xs text-gray-500">{page}/{data.pagination.totalPages}</span>
                  <Button variant="ghost" size="sm" disabled={page >= data.pagination.totalPages} onClick={() => setPage((p) => p + 1)}>Sig.</Button>
                </div>
              )}
            </Card>
          </div>

          {/* Detail Panel */}
          <div className="lg:col-span-2">
            {selectedRecipeId ? (
              <RecipeDetailPanel recipeId={selectedRecipeId} canEdit={canEdit} canEditName={canEditName} />
            ) : (
              <Card>
                <div className="px-6 py-16 text-center">
                  <BookOpen size={48} className="mx-auto text-gray-300" />
                  <p className="mt-4 text-sm text-gray-400">Selecciona un APU para ver el detalle</p>
                </div>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* Mobile FAB */}
      {canCreate && (
        <button
          className="fixed bottom-6 right-6 flex h-14 w-14 items-center justify-center rounded-full bg-[#dd5113] text-white shadow-lg transition-transform hover:scale-105 sm:hidden"
          onClick={() => setShowCreateModal(true)}
          aria-label="Nuevo APU"
        >
          <Plus size={24} />
        </button>
      )}

      <CreateRecipeModal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} />
    </div>
  );
}

// ── Recipe List Item ────────────────────────────────────────────────
function RecipeListItem({ recipe }: { recipe: RecipeListDTO }) {
  const total = recipe.precio_mo_base + recipe.precio_materiales_base;
  const divisor = 1 - recipe.porcentaje_utilidad_base / 100;
  const totalWithUtility = divisor > 0 ? total / divisor : total;

  return (
    <div>
      <div className="flex items-center justify-between">
        <p className="font-medium text-gray-800">{recipe.nombre}</p>
        <Badge color={getCategoriaColor(recipe.categoria)}>{recipe.categoria}</Badge>
      </div>
      <div className="mt-1 flex items-center gap-2 text-xs text-gray-400">
        <span>{recipe.codigo}</span>
        <span>·</span>
        <span>{recipe.materials_count} materiales</span>
        <span>·</span>
        <span className="font-medium text-gray-600">${totalWithUtility.toFixed(2)}/{recipe.unidad}</span>
      </div>
    </div>
  );
}

// ── Recipe Detail Panel ─────────────────────────────────────────────
function RecipeDetailPanel({ recipeId, canEdit, canEditName }: { recipeId: string; canEdit: boolean; canEditName: boolean }) {
  const { data: recipe, isLoading } = useRecipe(recipeId);
  const removeMutation = useRemoveRecipeMaterial();
  const updateMaterialMutation = useUpdateRecipeMaterial();
  const updateMutation = useUpdateRecipe();
  const deleteRecipeMutation = useDeleteRecipe();
  const [showAddMaterial, setShowAddMaterial] = useState(false);

  // Inline editable state for nombre
  const [editingNombre, setEditingNombre] = useState(false);
  const [nombreValue, setNombreValue] = useState('');

  // Inline editable state for categoria and unidad
  const [editingCategoria, setEditingCategoria] = useState(false);
  const [categoriaValue, setCategoriaValue] = useState('');
  const [editingUnidad, setEditingUnidad] = useState(false);
  const [unidadValue, setUnidadValue] = useState('');

  // Inline editable state for MO and utilidad
  const [editingMo, setEditingMo] = useState(false);
  const [moValue, setMoValue] = useState('');
  const [editingUtil, setEditingUtil] = useState(false);
  const [utilValue, setUtilValue] = useState('');

  // Inline editable state for material quantity
  const [editingMaterialId, setEditingMaterialId] = useState<string | null>(null);
  const [cantidadValue, setCantidadValue] = useState('');

  const saveCantidad = async (materialId: string) => {
    setEditingMaterialId(null);
    const val = parseFloat(cantidadValue);
    if (isNaN(val) || val <= 0) return;
    const material = recipe?.materials.find((m) => m.material_id === materialId);
    if (!material || val === material.cantidad_por_unidad) return;
    try {
      await updateMaterialMutation.mutateAsync({
        recipeId,
        materialId,
        data: { cantidadPorUnidad: val },
      });
    } catch {
      toast('error', 'Error al actualizar cantidad');
    }
  };

  if (isLoading || !recipe) {
    return <Card><div className="p-6"><SkeletonTable rows={3} cols={4} /></div></Card>;
  }

  const subtotal = recipe.precio_mo_base + recipe.precio_materiales_base;
  const divisor = 1 - recipe.porcentaje_utilidad_base / 100;
  const grandTotal = divisor > 0 ? subtotal / divisor : subtotal;
  const utilityAmount = grandTotal - subtotal;

  const saveNombre = async () => {
    setEditingNombre(false);
    const val = nombreValue.trim();
    if (!val || val === recipe.nombre) return;
    try {
      await updateMutation.mutateAsync({ id: recipe.id, data: { nombre: val } });
    } catch {
      toast('error', 'Error al guardar nombre de APU');
    }
  };

  const saveCategoria = async (newCategoria: string) => {
    setEditingCategoria(false);
    if (!newCategoria || newCategoria === recipe.categoria) return;
    const newCodigo = generateItemCode(newCategoria, recipe.nombre);
    try {
      await updateMutation.mutateAsync({
        id: recipe.id,
        data: {
          categoria: newCategoria as 'gypsum' | 'pintura' | 'empaste' | 'impermeabilizacion' | 'general',
          codigo: newCodigo,
        },
      });
    } catch {
      toast('error', 'Error al guardar categoría del APU');
    }
  };

  const saveUnidad = async (newUnidad: string) => {
    setEditingUnidad(false);
    if (!newUnidad || newUnidad === recipe.unidad) return;
    try {
      await updateMutation.mutateAsync({
        id: recipe.id,
        data: { unidad: newUnidad as 'm2' | 'ml' | 'unid' | 'kg' | 'glb' },
      });
    } catch {
      toast('error', 'Error al guardar unidad del APU');
    }
  };

  const saveMo = async () => {
    setEditingMo(false);
    const val = parseFloat(moValue);
    if (isNaN(val) || val === recipe.precio_mo_base) return;
    try {
      await updateMutation.mutateAsync({ id: recipe.id, data: { precioMoBase: val } });
    } catch {
      toast('error', 'Error al guardar precio M.O.');
    }
  };

  const saveUtil = async () => {
    setEditingUtil(false);
    const val = parseFloat(utilValue);
    if (isNaN(val) || val === recipe.porcentaje_utilidad_base) return;
    try {
      await updateMutation.mutateAsync({ id: recipe.id, data: { porcentajeUtilidadBase: val } });
    } catch {
      toast('error', 'Error al guardar utilidad.');
    }
  };

  return (
    <Card>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            {canEditName && editingNombre ? (
              <input
                type="text"
                autoFocus
                className="rounded border border-brand-blue px-2 py-0.5 text-lg font-semibold text-gray-800 focus:outline-none"
                value={nombreValue}
                onChange={(e) => setNombreValue(e.target.value)}
                onBlur={saveNombre}
                onKeyDown={(e) => { if (e.key === 'Enter') saveNombre(); if (e.key === 'Escape') setEditingNombre(false); }}
              />
            ) : (
              <h2
                className={`text-lg font-semibold text-gray-800 ${canEditName ? 'cursor-pointer hover:text-brand-blue' : ''}`}
                title={canEditName ? 'Click para editar nombre' : undefined}
                onClick={() => { if (canEditName) { setNombreValue(recipe.nombre); setEditingNombre(true); } }}
              >
                {recipe.nombre}
                {canEditName && <span className="ml-1 text-xs text-gray-300">✎</span>}
              </h2>
            )}
            <div className="mt-1 flex items-center gap-2">
              <span className="text-sm text-gray-400 font-mono">{recipe.codigo}</span>
              <span className="text-gray-300">—</span>
              {/* Unidad — editable for canEditName (admin + residente) */}
              {canEditName && editingUnidad ? (
                <select
                  autoFocus
                  className="rounded border border-brand-blue bg-white px-1.5 py-0.5 text-sm text-gray-700 focus:outline-none"
                  value={unidadValue}
                  onChange={(e) => { saveUnidad(e.target.value); }}
                  onBlur={() => setEditingUnidad(false)}
                >
                  {UNIDAD_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              ) : (
                <span
                  className={`text-sm text-gray-400 ${canEditName ? 'cursor-pointer hover:text-brand-blue' : ''}`}
                  title={canEditName ? 'Click para editar unidad' : undefined}
                  onClick={() => { if (canEditName) { setUnidadValue(recipe.unidad); setEditingUnidad(true); } }}
                >
                  {recipe.unidad}
                  {canEditName && <span className="ml-1 text-xs text-gray-300">✎</span>}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Categoria — editable for canEditName (admin + residente) */}
            {canEditName && editingCategoria ? (
              <select
                autoFocus
                className="rounded border border-brand-blue bg-white px-1.5 py-0.5 text-sm text-gray-700 focus:outline-none"
                value={categoriaValue}
                onChange={(e) => { saveCategoria(e.target.value); }}
                onBlur={() => setEditingCategoria(false)}
              >
                {CATEGORIA_OPTIONS_LOCAL.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            ) : (
              <div
                className={canEditName ? 'cursor-pointer' : ''}
                title={canEditName ? 'Click para editar categoría (regenera el código)' : undefined}
                onClick={() => { if (canEditName) { setCategoriaValue(recipe.categoria); setEditingCategoria(true); } }}
              >
                <Badge color={getCategoriaColor(recipe.categoria)}>{recipe.categoria}</Badge>
                {canEditName && <span className="ml-1 text-xs text-gray-300">✎</span>}
              </div>
            )}
            {canEdit && (
              <button
                className="text-gray-400 hover:text-red-500"
                title="Desactivar APU"
                onClick={async () => {
                  if (window.confirm('¿Desactivar este APU? No aparecerá en futuras búsquedas.')) {
                    await deleteRecipeMutation.mutateAsync(recipe.id);
                  }
                }}
              >
                <Trash2 size={16} />
              </button>
            )}
          </div>
        </div>

        {/* Price Summary */}
        <div className="mt-4 grid grid-cols-2 gap-3 rounded-lg bg-gray-50 p-4 sm:grid-cols-4">
          {/* MO — auto-calculado desde items MDO */}
          <div>
            <p className="text-xs text-gray-500">Mano de Obra <span className="text-green-500">(auto)</span></p>
            <p className="text-sm font-semibold text-gray-800">${recipe.precio_mo_base.toFixed(2)}</p>
          </div>

          {/* Materiales — auto-calculado */}
          <div>
            <p className="text-xs text-gray-500">Materiales <span className="text-green-500">(auto)</span></p>
            <p className="text-sm font-semibold text-gray-800">${recipe.precio_materiales_base.toFixed(2)}</p>
          </div>

          {/* Utilidad — editable */}
          <div>
            <p className="text-xs text-gray-500">Utilidad</p>
            {canEdit && editingUtil ? (
              <input
                type="number"
                step="1"
                min="0"
                max="100"
                autoFocus
                className="mt-0.5 w-full rounded border border-brand-blue px-2 py-0.5 text-sm font-semibold focus:outline-none"
                value={utilValue}
                onChange={(e) => setUtilValue(e.target.value)}
                onBlur={saveUtil}
                onKeyDown={(e) => { if (e.key === 'Enter') saveUtil(); if (e.key === 'Escape') setEditingUtil(false); }}
              />
            ) : (
              <p
                className={`text-sm font-semibold text-gray-800 ${canEdit ? 'cursor-pointer hover:text-brand-blue' : ''}`}
                title={canEdit ? 'Click para editar' : undefined}
                onClick={() => { if (canEdit) { setUtilValue(String(recipe.porcentaje_utilidad_base)); setEditingUtil(true); } }}
              >
                {recipe.porcentaje_utilidad_base}% (${utilityAmount.toFixed(2)})
                {canEdit && <span className="ml-1 text-xs text-gray-300">✎</span>}
              </p>
            )}
          </div>

          {/* Total */}
          <div>
            <p className="text-xs text-gray-500">Total</p>
            <p className="text-sm font-bold text-brand-blue">${grandTotal.toFixed(2)}</p>
          </div>
        </div>

        {canEdit && (
          <p className="mt-1.5 text-xs text-gray-400">
            Materiales se calcula automáticamente. Haz click en M.O. o Utilidad para editar.
          </p>
        )}

        {/* Materials Table */}
        <div className="mt-6">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-700">Materiales y Mano de Obra</h3>
            {canEdit && (
              <Button variant="secondary" size="sm" leftIcon={<Plus size={14} />} onClick={() => setShowAddMaterial(true)}>
                Agregar
              </Button>
            )}
          </div>

          {recipe.materials.length === 0 ? (
            <p className="mt-4 text-center text-sm text-gray-400">Sin materiales asignados</p>
          ) : (
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    <th className="px-3 py-2">Item</th>
                    <th className="px-3 py-2">Cantidad</th>
                    <th className="px-3 py-2">Precio Unit.</th>
                    <th className="px-3 py-2">Subtotal</th>
                    {canEdit && <th className="px-3 py-2 w-10"></th>}
                  </tr>
                </thead>
                <tbody>
                  {recipe.materials.map((m) => (
                    <tr key={m.id} className={`border-b border-gray-50 ${m.is_mdo ? 'bg-blue-50/50' : ''}`}>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-gray-800">{m.material_nombre}</p>
                          {m.is_mdo && <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium text-blue-700">MDO</span>}
                        </div>
                        <p className="text-xs text-gray-400">{m.material_sku ?? ''} · {m.material_unidad}</p>
                      </td>
                      {/* CANTIDAD / PRECIO columns:
                          For materials with rendimiento (paints, coatings):
                            - show cantidad=1 (conceptual "1 m² of this material")
                            - show precio = precio_unitario / rendimiento (effective $/m²)
                          For gypsum / MDO materials (no rendimiento):
                            - show raw cantidad and original price
                      */}
                      {m.material_rendimiento && m.material_rendimiento > 0 ? (
                        <>
                          <td className="px-3 py-2 text-gray-400 text-xs">
                            <span title={`Físico: ${m.cantidad_por_unidad} ${m.material_unidad} por m² de APU`}>
                              1 m²
                            </span>
                          </td>
                          <td className="px-3 py-2 text-gray-600">
                            ${(m.material_precio_unitario / m.material_rendimiento).toFixed(4)}
                            <span className="ml-1 text-[10px] text-gray-400" title={`$${m.material_precio_unitario.toFixed(2)} / ${m.material_rendimiento} ${m.material_rendimiento_unidad ?? 'm²'}`}>
                              /m²
                            </span>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-3 py-2 text-gray-600">
                            {canEdit && editingMaterialId === m.material_id ? (
                              <input
                                type="number"
                                step="0.0001"
                                min="0.0001"
                                autoFocus
                                className="w-20 rounded border border-brand-blue px-2 py-0.5 text-sm font-medium focus:outline-none"
                                value={cantidadValue}
                                onChange={(e) => setCantidadValue(e.target.value)}
                                onBlur={() => saveCantidad(m.material_id)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') saveCantidad(m.material_id);
                                  if (e.key === 'Escape') setEditingMaterialId(null);
                                }}
                              />
                            ) : (
                              <span
                                className={canEdit ? 'cursor-pointer hover:text-brand-blue' : ''}
                                title={canEdit ? 'Click para editar cantidad' : undefined}
                                onClick={() => {
                                  if (canEdit) {
                                    setCantidadValue(String(m.cantidad_por_unidad));
                                    setEditingMaterialId(m.material_id);
                                  }
                                }}
                              >
                                {m.cantidad_por_unidad}
                                {canEdit && <span className="ml-1 text-xs text-gray-300">&#9998;</span>}
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-gray-600">${m.material_precio_unitario.toFixed(2)}</td>
                        </>
                      )}
                      <td className="px-3 py-2 font-medium text-gray-800">
                        ${(m.material_precio_unitario * m.cantidad_por_unidad).toFixed(2)}
                      </td>
                      {canEdit && (
                        <td className="px-3 py-2">
                          <button
                            className="text-gray-400 hover:text-red-500"
                            onClick={() => removeMutation.mutate({ recipeId: recipe.id, materialId: m.material_id })}
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showAddMaterial && (
        <AddMaterialModal
          isOpen={showAddMaterial}
          onClose={() => setShowAddMaterial(false)}
          recipeId={recipe.id}
          existingMaterialIds={recipe.materials.map((m) => m.material_id)}
        />
      )}
    </Card>
  );
}

// ── Add Material Modal ──────────────────────────────────────────────
function AddMaterialModal({
  isOpen,
  onClose,
  recipeId,
  existingMaterialIds,
}: {
  isOpen: boolean;
  onClose: () => void;
  recipeId: string;
  existingMaterialIds: string[];
}) {
  const addMutation = useAddRecipeMaterial();

  type SelMat = { id: string; nombre: string; unidad: string; precio_unitario: number; rendimiento: number | null };

  // Regular materials section state
  const [matSearch, setMatSearch] = useState('');
  const [selectedMat, setSelectedMat] = useState<SelMat | null>(null);
  const [cantidadMat, setCantidadMat] = useState('');

  // MDO section state
  const [mdoSearch, setMdoSearch] = useState('');
  const [selectedMdo, setSelectedMdo] = useState<SelMat | null>(null);
  const [cantidadMdo, setCantidadMdo] = useState('');

  const { data: matsData } = useMaterials({ search: matSearch || undefined, limit: 30 });
  const regularMaterials: SelMat[] = (matsData?.data ?? []).filter(
    (m) => m.categoria !== 'mdo' && !existingMaterialIds.includes(m.id),
  ).map((m) => ({ id: m.id, nombre: m.nombre, unidad: m.unidad, precio_unitario: m.precio_unitario, rendimiento: m.rendimiento }));

  const { data: mdoData } = useMaterials({ search: mdoSearch || undefined, categoria: 'mdo', limit: 30 });
  const mdoMaterials: SelMat[] = (mdoData?.data ?? []).filter((m) => !existingMaterialIds.includes(m.id))
    .map((m) => ({ id: m.id, nombre: m.nombre, unidad: m.unidad, precio_unitario: m.precio_unitario, rendimiento: m.rendimiento }));

  const handleAdd = async (
    material: { id: string; nombre: string },
    cantidad: string,
    clearFn: () => void,
  ) => {
    const qty = parseFloat(cantidad);
    if (!qty || qty <= 0) {
      toast('error', 'La cantidad debe ser mayor a 0');
      return;
    }
    await addMutation.mutateAsync({
      recipeId,
      data: { materialId: material.id, cantidadPorUnidad: qty },
    });
    clearFn();
    // Don't close — let user add multiple items
  };

  return (
    <Modal isOpen={isOpen} title="Agregar ítems al APU" onClose={onClose} size="lg">
      <div className="space-y-5">
        {/* ── Materiales section ── */}
        <SearchAddSection
          title="Material"
          search={matSearch}
          setSearch={setMatSearch}
          selected={selectedMat}
          setSelected={setSelectedMat}
          cantidad={cantidadMat}
          setCantidad={setCantidadMat}
          suggestions={regularMaterials}
          onAdd={() =>
            handleAdd(selectedMat!, cantidadMat, () => {
              setSelectedMat(null);
              setCantidadMat('');
              setMatSearch('');
            })
          }
          isPending={addMutation.isPending}
          placeholder="Buscar material por nombre..."
          accentColor="blue"
        />

        {/* ── Mano de Obra section ── */}
        <SearchAddSection
          title="Mano de Obra"
          search={mdoSearch}
          setSearch={setMdoSearch}
          selected={selectedMdo}
          setSelected={setSelectedMdo}
          cantidad={cantidadMdo}
          setCantidad={setCantidadMdo}
          suggestions={mdoMaterials}
          onAdd={() =>
            handleAdd(selectedMdo!, cantidadMdo, () => {
              setSelectedMdo(null);
              setCantidadMdo('');
              setMdoSearch('');
            })
          }
          isPending={addMutation.isPending}
          placeholder="Buscar ítem de mano de obra..."
          accentColor="amber"
        />

        <div className="flex justify-end pt-1">
          <Button variant="primary" onClick={onClose}>Cerrar</Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Shared search-and-add section used by AddMaterialModal ──────────
function SearchAddSection({
  title, search, setSearch, selected, setSelected,
  cantidad, setCantidad, suggestions, onAdd, isPending, placeholder, accentColor,
}: {
  title: string;
  search: string; setSearch: (v: string) => void;
  selected: { id: string; nombre: string; unidad: string; precio_unitario: number; rendimiento: number | null } | null;
  setSelected: (m: { id: string; nombre: string; unidad: string; precio_unitario: number; rendimiento: number | null } | null) => void;
  cantidad: string; setCantidad: (v: string) => void;
  suggestions: Array<{ id: string; nombre: string; unidad: string; precio_unitario: number; rendimiento: number | null }>;
  onAdd: () => void;
  isPending: boolean;
  placeholder: string;
  accentColor: 'blue' | 'amber';
}) {
  const borderClass = accentColor === 'amber'
    ? 'border-amber-200 bg-amber-50/40'
    : 'border-blue-100 bg-blue-50/30';

  return (
    <div className="space-y-2">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-700">{title}</p>
      <div className={`rounded-lg border p-3 space-y-3 ${borderClass}`}>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={selected ? selected.nombre : search}
            onChange={(e) => { setSearch(e.target.value); setSelected(null); }}
            placeholder={placeholder}
            className="w-full rounded-md border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
          />
          {selected && (
            <button
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              onClick={() => { setSelected(null); setSearch(''); }}
            >
              <ChevronDown size={14} />
            </button>
          )}
        </div>

        {search && !selected && suggestions.length > 0 && (
          <div className="max-h-36 overflow-y-auto rounded-md border border-gray-200 bg-white">
            {suggestions.map((mat) => (
              <button
                key={mat.id}
                onClick={() => {
                  setSelected(mat);
                  setSearch('');
                  // Auto-suggest cantidad = 1/rendimiento when material has yield data
                  if (mat.rendimiento && mat.rendimiento > 0) {
                    setCantidad(String(Math.round((1 / mat.rendimiento) * 1000000) / 1000000));
                  }
                }}
                className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-gray-50"
              >
                <span className="text-gray-700">{mat.nombre}</span>
                <div className="text-right">
                  <span className="text-xs text-gray-400">${mat.precio_unitario.toFixed(2)} / {mat.unidad}</span>
                  {mat.rendimiento && mat.rendimiento > 0 && (
                    <span className="ml-2 text-xs text-blue-400">rend. {mat.rendimiento}m²</span>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}

        {search && !selected && suggestions.length === 0 && (
          <p className="text-xs text-gray-400 text-center py-1">Sin resultados</p>
        )}

        {selected && (
          <div className="space-y-2">
            {selected.rendimiento && selected.rendimiento > 0 && (
              <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800">
                <p className="font-semibold">Material con rendimiento ({selected.rendimiento} {accentColor === 'blue' ? 'm²' : 'ml'} por unidad)</p>
                <p className="mt-0.5">
                  Costo por m² de APU: <span className="font-bold">${(selected.precio_unitario / selected.rendimiento).toFixed(4)}</span>
                  {' '}(${selected.precio_unitario.toFixed(2)} ÷ {selected.rendimiento})
                </p>
                <p className="mt-0.5 text-blue-600">La cantidad se calcula automáticamente como 1 / rendimiento.</p>
              </div>
            )}
          {selected.rendimiento && selected.rendimiento > 0 ? (
            /* Material with rendimiento: quantity is fixed (1/rendimiento), just show the Add button */
            <Button
              variant="primary"
              size="sm"
              onClick={onAdd}
              isLoading={isPending}
              leftIcon={<Plus size={14} />}
            >
              Agregar
            </Button>
          ) : (
            /* Material without rendimiento: user enters quantity manually */
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <label className="mb-1 block text-xs text-gray-500">Cantidad por unidad de APU</label>
                <input
                  type="number"
                  step="0.0001"
                  min="0"
                  value={cantidad}
                  onChange={(e) => setCantidad(e.target.value)}
                  placeholder="0.0000"
                  className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-brand-blue focus:outline-none"
                />
              </div>
              <Button
                variant="primary"
                size="sm"
                onClick={onAdd}
                isLoading={isPending}
                leftIcon={<Plus size={14} />}
              >
                Agregar
              </Button>
            </div>
          )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Create Recipe Modal ─────────────────────────────────────────────
function CreateRecipeModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const createMutation = useCreateRecipe();
  const [nombre, setNombre] = useState('');
  const [categoria, setCategoria] = useState('gypsum');
  const [unidad, setUnidad] = useState('m2');

  const codigo = generateItemCode(categoria, nombre);

  const handleSubmit = async () => {
    if (!nombre.trim()) return;
    await createMutation.mutateAsync({
      codigo,
      nombre: nombre.trim().toUpperCase(),
      categoria: categoria as 'gypsum' | 'pintura' | 'empaste' | 'impermeabilizacion' | 'general',
      unidad: unidad as 'm2' | 'ml' | 'unid' | 'kg' | 'glb',
      precioMoBase: 0,
      precioMaterialesBase: 0,
      porcentajeUtilidadBase: 0,
    });
    setNombre('');
    setCategoria('gypsum');
    setUnidad('m2');
    onClose();
  };

  return (
    <Modal isOpen={isOpen} title="Nuevo APU" onClose={onClose} size="md">
      <div className="space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Código (auto-generado)</label>
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-sm text-gray-500">
            {codigo || '---'}
          </div>
        </div>

        <Input
          label="Nombre"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          placeholder="Ej: TUMBADO REGULAR"
        />

        <div className="grid grid-cols-2 gap-4">
          <Select
            label="Categoría"
            value={categoria}
            onChange={(e) => setCategoria(e.target.value)}
            options={CATEGORIA_OPTIONS_LOCAL}
          />
          <Select
            label="Unidad"
            value={unidad}
            onChange={(e) => setUnidad(e.target.value)}
            options={UNIDAD_OPTIONS}
          />
        </div>

        <p className="text-xs text-gray-400">
          Los precios (M.O., materiales, utilidad) se configuran en el panel de detalle una vez creado el APU y agregados los materiales.
        </p>

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" onClick={onClose} type="button">Cancelar</Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            isLoading={createMutation.isPending}
            disabled={!nombre.trim()}
          >
            Crear APU
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function getCategoriaColor(categoria: string): 'blue' | 'green' | 'orange' | 'gray' {
  switch (categoria) {
    case 'gypsum': return 'blue';
    case 'pintura': return 'green';
    case 'impermeabilizacion': return 'orange';
    default: return 'gray';
  }
}
